import { Avatar, Box, Button, Center, Column, Row, Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { generatePath } from 'react-router';
import { hideEodInvitePopup, SHOW_MESSAGE, showEodInvitePopup } from '~/actions';
import browserHistory from '~/browserHistory';
import { colors } from '@tempmee/tailwind';
import Portal from '~/commonComponents/Portal';
import { IEligibleJob } from '~/domain/EligibleJobs';
import {
  IInviteProfessionalsToJobsPayload,
  jobKeys,
  useInviteProfessionalsToJobsMutation,
} from '~/domain/Jobs';
import { IUser } from '~/models';
import {
  fireEodInvitePopupViewed,
  fireEodNoThanksClicked,
  fireInviteSent,
  fireShiftSelected,
} from '~/posthogs/events';
import { routes } from '~/routes';
import { EligibleJobCard } from '~/components/EligibleJobCard';
import { OutlineXIcon } from '@tempmee/react-icons';

interface IRatingSentSuccessPopupProps {
  eligibleJobs: IEligibleJob[];
  professional: IUser;
  officeId: string;
  onClose: () => void;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onSubmitFeedback: () => void;
}

export const RatingSentSuccessPopup = ({
  eligibleJobs,
  professional,
  officeId,
  onClose,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onSubmitFeedback,
}: IRatingSentSuccessPopupProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isInviting = inviteProfessionalsToJobsMutation.isPending;

  useEffect(() => {
    fireEodInvitePopupViewed({ professionalId: professional?.id });
  }, [professional?.id]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleNoThanks = useCallback(() => {
    fireEodNoThanksClicked({ professionalId: professional?.id });
    dispatch(hideEodInvitePopup());
    onClose();
  }, [professional?.id, onClose, dispatch]);

  const handleJobSelect = useCallback(
    (jobId: string) => {
      setSelectedJobIds((prev) => {
        if (prev.includes(jobId)) {
          return prev.filter((id) => id !== jobId);
        }

        fireShiftSelected({
          shiftId: jobId,
          shiftStatus:
            eligibleJobs.find((job) => job.id === jobId)?.status === 'open'
              ? 'Open'
              : 'Action Needed',
        });

        return [...prev, jobId];
      });
    },
    [eligibleJobs],
  );

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (scrolledToBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handlePopupClose = useCallback(() => {
    setSelectedJobIds([]);
    dispatch(hideEodInvitePopup());
    onClose();
  }, [onClose, dispatch]);

  const handleInviteSuccess = useCallback(
    (_data: unknown, payload: IInviteProfessionalsToJobsPayload) => {
      payload.jobIds.forEach((jobId) => {
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      });
      handlePopupClose();
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          isSuccess: true,
          popupProps: {
            rightButtonText: 'Close',
          },
          route: '/',
          title: 'Request(s) Successfully Sent!',
          message: (
            <>
              <b>
                {professional?.first_name} {professional?.last_name}
              </b>{' '}
              has been invited to the selected shift(s). Once they accept at the listed rate,{' '}
              {professional?.first_name} will be confirmed!
            </>
          ),
        },
      });
    },
    [dispatch, handlePopupClose, professional, queryClient],
  );

  const handleInviteClick = () => {
    if (selectedJobIds.length > 0) {
      onSubmitFeedback();

      fireInviteSent({
        professionalId: professional?.id,
        totalInvitesSent: selectedJobIds.length,
        source: 'EOD Invite Pop-up',
      });

      inviteProfessionalsToJobsMutation.mutate(
        {
          professionalIds: [professional.id],
          jobIds: selectedJobIds,
          officeId,
        },
        {
          onSuccess: handleInviteSuccess,
        },
      );
    }
  };

  const onCardPress = (jobId: string) => {
    dispatch(showEodInvitePopup());
    browserHistory.push(generatePath(routes.jobDetails, { jobId }));
  };

  return (
    <Portal>
      <Column
        className="modal"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        }}
      >
        <Column
          className="modal_content"
          style={{
            position: 'relative',
            padding: '40px 30px',
            maxWidth: 700,
            maxHeight: '90vh',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Box
            onClick={handleNoThanks}
            data-testid="close-popup"
            style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}
          >
            <OutlineXIcon />
          </Box>

          <Typography as="span" variant="body1" bold>
            Rating Sent Successfully!
          </Typography>

          <Column align="center" style={{ marginTop: 12, marginBottom: 16 }}>
            <Avatar size="xl" src={professional?.profile_image || undefined} alt="profile_image" />
          </Column>

          <Typography as="span" color={colors.neutral[500]} textAlign="center">
            Sounds like{' '}
            <Typography as="span" bold color={colors.neutral[500]}>
              {professional?.first_name?.length > 20
                ? professional?.first_name?.slice(0, 20) + '...'
                : professional?.first_name}
            </Typography>{' '}
            was a great fit.
            <br />
            Would you like to invite them to upcoming shifts?
          </Typography>

          <Column
            ref={scrollContainerRef}
            className="flex-1"
            style={{
              marginTop: 16,
              marginBottom: 24,
              overflowY: 'auto',
              overflowX: 'hidden',
              maxHeight: 400,
              scrollbarWidth: 'thin',
            }}
          >
            {isLoading ? (
              <Center direction="column" p={10}>
                <CenteredSpinner />
              </Center>
            ) : (
              <Column gap={2} pb={2} style={{ marginInline: 8, marginTop: 4 }}>
                {eligibleJobs.map((job) => (
                  <EligibleJobCard
                    key={job.id}
                    job={job}
                    isSelected={selectedJobIds.includes(job.id)}
                    onSelect={handleJobSelect}
                    disabled={isInviting}
                    onCardPress={onCardPress}
                  />
                ))}

                {isFetchingNextPage && (
                  <Center direction="column" p={4}>
                    <CenteredSpinner />
                  </Center>
                )}
              </Column>
            )}
          </Column>

          <Row gap={4} justify="center" style={{ marginTop: 16 }}>
            <Button
              data-cy="rating-success-no-thanks-button"
              variant="outlined"
              size="md"
              onClick={handleNoThanks}
              disabled={isInviting}
            >
              No Thanks
            </Button>

            <Button
              data-cy="rating-success-invite-button"
              onClick={handleInviteClick}
              disabled={selectedJobIds.length === 0}
              size="md"
              isLoading={isInviting}
            >
              {selectedJobIds.length > 0
                ? `Invite for ${selectedJobIds.length} Shift${selectedJobIds.length !== 1 ? 's' : ''}`
                : 'Invite'}
            </Button>
          </Row>
        </Column>
      </Column>
    </Portal>
  );
};
